<template>
    <!-- The v-if checks for equality of the current results and potential new queries.-->
    <ag-grid-vue v-if="JSON.stringify(row_keys) == JSON.stringify(Object.keys(current_genes))" class="ag-theme-alpine"
        :columnDefs="column_defs" :defaultColDef="default_cols" :rowData="row_data" :rowStyle="row_style"
        :style="`height: ${grid_height}px; width: auto;`" rowHeight="60" :alwaysShowHorizontalScroll="false"
        :pagination="true" :paginationPageSize="14" :gridOptions="gridOptions" @grid-ready="onGridReady" />
</template>

<script setup lang="js">
import { AgGridVue } from 'ag-grid-vue3';
import { onMounted, onUnmounted, ref, computed, watch, h, render } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import StarButton from '@/components/tables/StarButton.vue';
import TooltipHeader from '@/components/tables/TooltipHeader.vue';
import agg from '@/methods/ag_grid_toolbox';

const store = useStore();
const router = useRouter();

const selected_variants = computed(() => store.state.presentation_selected_variants);
const current_genes = computed(() => store.getters.genes);
const column_defs = computed(() => store.state.ag_grid_coldefs[props?.table_id]);
const row_data = computed(() => store.state.ag_grid_rowdata[props?.table_id]);
const row_keys = computed(() => store.state.ag_grid_rowkeys[props?.table_id]);
const devmode = computed(() => store.state.devmode);

const default_cols = ref()
/* Grid height is set to 14*60 + 100 (14 rows)*/
const grid_height = computed(() => {
    var temp_height = Math.min(940, (row_keys.value.length + 1) * 60 + 40);
    if (props?.maxHeight) {
        temp_height = Math.min(props?.maxHeight, temp_height);
        gridOptions.api?.paginationSetPageSize(Math.floor((temp_height - 100) / 60))
    }
    return temp_height;
})

const gridOptions = {};

/**
 * @prop preferSelected - If this is set to true, only selected variants will be shown in the table.
 * @prop showSelectColumn - Dictates whether to show the select column (i.e. the blue star) on this table.
 * @prop maxHeight - Can be passed to limit this table's maximum height (in px).
 * @prop table_id - ID for the table. Important for HTML query selections, so it should be unique.
 */
const props = defineProps({
    preferSelected: Boolean,
    showSelectColumn: Boolean,
    maxHeight: Number,
    table_id: String,
})

/**
 * When this table is loaded, the following steps are performed:
 * 1. Default column definitions, set in ag_grid_toolbox.js, are used.
 * 2. Specific column definitions for genes are created.
 * 3. Gene-related data is turned into an AG Grid RowData object.
 * 4. Columns are resized and the page is set to 0.
 * 5. Event listeners for sorting and resizing are added.
 */
onMounted(() => {
    if (current_genes.value) {
        default_cols.value = agg.createDefaultColDefs();
        createColDefs();
        genesToRowData();
        gridOptions.api?.sizeColumnsToFit();
        gridOptions.api?.paginationGoToPage(0);
        window.addEventListener('resize', resizeGrid);
        window.addEventListener('sort-grid', (e) => onSort(e));
    }
});

/**
 * Event listeners for sorting and resizing are removed on unmounting the component.
 */
onUnmounted(() => {
    window.removeEventListener('resize', resizeGrid);
    window.removeEventListener('sort-grid', (e) => onSort(e))
})

/**
 * Called when the sort-grid window event is caught. Applies a specific sort to the grid and refreshes it.
 * Will sort based on a targeted column based on a provided sorting method (asc, desc, none)
 * @param {CustomEvent} e - Event that contains sorting information (column, desired order)
 */
const onSort = (e) => {
    gridOptions.columnApi?.applyColumnState({
        state: [{ colId: e.detail.column.toLowerCase(), sort: e.detail.order }],
        defaultState: { sort: null },
    });
    gridOptions.api?.refreshCells();
}

/**
 * Once the grid is ready, it is refreshed and the select column is hidden if indicated by the props.
 * Then, it is resized.
 */
const onGridReady = () => {
    gridOptions.api?.refreshCells();
    if (!props.showSelectColumn) {
        gridOptions.columnApi?.setColumnVisible('select', false)
    }
    resizeGrid();
}

/**
 * Resizes/re-renders specific elements of the grid based on the screen width.
 * Sizes should be set in accordance with any media queries.
 */
const resizeGrid = () => {
    gridOptions.api?.sizeColumnsToFit();
    let current_coldefs = gridOptions.api?.getColumnDefs();

    // Sizes below 1300
    if (window.innerWidth < 1300) {
        current_coldefs.forEach((def, _) => {
            def.headerComponent = null;
            def.headerName = shortened_headers[def.colId];
            if (def.colId == 'position') def.cellRenderer = (param) => renderPosition(param?.data.position, 'small')
            // Sizes below 1000
            if (window.innerWidth < 1000) {
                if (def.colId == 'details') {
                    def.width = '60';
                    def.maxWidth = '60';
                    def.minWidth = '60';
                    def.cellRenderer = (param) => addRouterLink(param, 'small')
                }
                if (def.colId == 'select') {
                    def.width = '50';
                    def.maxWidth = '50';
                    def.minWidth = '50';
                }
            }
            // Sizes between 1000 and 1300
            else {
                if (def.colId == 'details') {
                    def.width = '80';
                    def.maxWidth = '80';
                    def.minWidth = '80';
                    def.cellRenderer = (param) => addRouterLink(param, 'small')
                }
                if (def.colId == 'select') {
                    def.width = '60';
                    def.maxWidth = '60';
                    def.minWidth = '60';
                }
            }
        });
    }
    // Normal size (> 1300)
    if (window.innerWidth > 1300) {
        current_coldefs.forEach((def, index) => {
            def.headerComponent = TooltipHeader;
            def.headerName = long_headers[def.colId];
            if (def.colId == 'position') def.cellRenderer = (param) => renderPosition(param?.data.position, 'full')
            if (def.colId == 'details') {
                def.width = '120';
                def.maxWidth = '120';
                def.minWidth = '120';
                def.cellRenderer = (param) => addRouterLink(param, 'full')
            }
            if (def.colId == 'select') {
                def.width = '100';
                def.maxWidth = '100';
                def.minWidth = '100';
            }
        })
    }
    gridOptions.api?.setColumnDefs(current_coldefs);
    gridOptions.api?.redrawRows();
}



/**
 * Creates table column definitions. These are specific for this table.
 * Saves the definitions in the column_defs ref (see below) and applies them to the grid.
 * If keys and key_values refs are defined below, they are used to automatically create some column definitions.
 */
const createColDefs = () => {

    let new_column_defs = [];

    // Selection Column
    if (props.showSelectColumn && devmode.value) {
        let s_width = (window.innerWidth < 1300 ? (window.innerWidth < 1000 ? '50' : '60') : '100')
        new_column_defs.push({
            headerName: 'Select', headerClass: 'presentation-header', colId: 'select',
            cellRenderer: (param) => {
                let container = document.createElement("div")
                const vsb = h(StarButton, {
                    id: param?.data.gene, onToggle: (id) => toggleSelectedGene(id),
                    selected: Object.keys(selected_variants.value).includes(param?.data.gene)
                });
                render(vsb, container);
                return container;
            },
            autoHeight: true, flex: 0, width: s_width, minWidth: s_width, maxWidth: s_width, pinned: 'left', sortable: 'false',
            headerComponent: TooltipHeader, headerComponentParams: { title: 'Select', table: props?.table_id }, cellStyle: { justifyContent: "center" }
        })
    }

    // Gene Column
    new_column_defs.push({
        headerName: 'Gene', colId: 'gene', headerClass: 'presentation-header-left', field: 'gene',
        headerComponent: TooltipHeader, headerComponentParams: { title: 'Gene', table: props?.table_id },
    });

    // Position Column
    new_column_defs.push({
        headerName: 'Position', colId: 'position', headerClass: 'presentation-header-left',
        headerComponent: TooltipHeader, headerComponentParams: { title: 'Position', table: props?.table_id, tooltip_id: 'position_gene' },
        cellStyle: { justifyContent: "flex-start", lineHeight: "100%", textAlign: "left" },
        cellRenderer: (params) => renderPosition(params?.data.position, 'full'),
    });

    // CIViC URL
    new_column_defs.push({
        headerName: 'CIViC Gene ID', colId: 'civic_url', headerClass: 'presentation-header-left',
        headerComponent: TooltipHeader, headerComponentParams: { title: 'CIViC Gene ID', tooltip_id: 'civic_gene' },
        cellRenderer: (params) => addExternalLink(params?.data.civic_id, params?.data.civic_url)
    });


    // Entrez ID
    new_column_defs.push({
        headerName: 'Entrez ID', colId: 'treatments', headerClass: 'presentation-header-left', field: 'entrez_id',
        headerComponent: TooltipHeader, headerComponentParams: { title: 'Entrez ID', table: props?.table_id, tooltip_id: 'entrez_id' },
    });

    // Treatments Column
    new_column_defs.push({
        headerName: 'Treatments', colId: 'treatments', headerClass: 'presentation-header-left', field: 'treatments',
        headerComponent: TooltipHeader, headerComponentParams: { title: 'Treatments', table: props?.table_id, tooltip_id: 'treatments_gene' },
    });


    // Details
    let d_width = (window.innerWidth < 1300 ? (window.innerWidth < 1000 ? '60' : '80') : '120')
    new_column_defs.push({
        headerName: 'Details', sortable: 'false', cellRenderer: (param) => addRouterLink(param, window.innerWidth < 1300 ? 'small' : 'full'),
        headerClass: 'presentation-header', pinned: 'right', flex: 0,
        width: d_width, maxWidth: d_width, minWidth: d_width, colId: 'details', cellStyle: { justifyContent: "center" },
        headerComponent: TooltipHeader, headerComponentParams: { title: 'Details', table: props?.table_id }
    });

    store.dispatch('setAGGColdefs', { defs: new_column_defs, id: props?.table_id });
    gridOptions.api?.setColumnDefs(new_column_defs);
}

/**
 * Toggles whether a gene is part of the 'selected variants/genes' subset or not.
 * @param {String} id - Gene identifier
 */
const toggleSelectedGene = async (id) => {
    store.dispatch('togglePresentationVariantSelected', id);
    gridOptions.api?.refreshCells();
}

/**
 * Adds a link to an external resource, e.g. a PubMed article. Will open in a new tab.
 * @param {String} id - What will be shown instead of the URL
 * @param {String} url - URL to go to
 */
const addExternalLink = (id, url) => {
    const link = document.createElement("a");
    link.style.textDecoration = "none";
    link.innerText = id;
    link.href = url;
    link.target = "_blank";
    return link;
}

/**
 * Adds a router-link button to the details column.
 * @param {object} param - AG Grid parameters 
 */
const addRouterLink = (param, size = 'full') => {
    const link = document.createElement("div");
    link.innerText = "Details";

    if (param.data.gene && param.data.gene != '') {
        link.classList.add("details-button");
    } else {
        link.classList.add("details-button-disabled");
    }
    link.classList.add("text-button");
    if (size == 'small') {
        link.innerText = 'D.'
        link.classList.add("details-button-small")
    }
    link.addEventListener("click", e => {
        e.preventDefault();
        if (param.data.gene && param.data.gene != '') {
            router.push(param.data.details_link.path);
        }
    });
    return link;
}

/**
 * Turns current gene data into rows for the AG Grid.
 * Uses two refs (see below): row_data and keys.
 */
const genesToRowData = () => {
    let list_to_use = current_genes.value;
    if (!list_to_use || !Object.keys(list_to_use).length) return [];
    let new_row_data = [];
    for (let gene of Object.keys(list_to_use)) {

        let new_row = { gene: gene };

        // Position
        let gene_data = agg.resolveDeepProperty(gene + '.gencode.gene', list_to_use);
        if (gene_data !== null && Array.isArray(gene_data)) {
            let gene_summary = gene_data[0];
            if (gene_data.length > 0) {
                new_row['position'] = {
                    chromosome: gene_summary[' seqname'],
                    start: gene_summary[' start_pos'],
                    end: gene_summary[' end_pos'],
                };
            }
        } else {
            new_row['position'] = {
                chromosome: 'Unknown',
                start: 'Unknown',
                end: 'Unknown',
            };
        }

        let civic_summary = agg.resolveDeepProperty(gene + '.civic', list_to_use);
        if (civic_summary !== null && typeof civic_summary === 'object') {
            // Treatments
            if ("any_mutation_in_gene" in civic_summary.civic) {
                new_row['treatments'] = civic_summary.civic['any_mutation_in_gene'].length;

                // CIViC URL + ID
                try {
                    if ('gene_summary' in civic_summary) {
                        if (civic_summary['gene_summary'].length > 0) {
                            new_row['civic_id'] = civic_summary['gene_summary'][0]['gene_id']
                            new_row['civic_url'] = civic_summary['gene_summary'][0]['gene_civic_url'];

                            // Entrez ID
                            new_row['entrez_id'] = civic_summary['gene_summary'][0]['entrez_id'];
                        }
                    }

                    // GENCODE

                } catch (error) {
                    console.log(error)
                }
            }
        }

        //Finally, generate link for details button and marked status:
        new_row['details_link'] = { path: `${store.getters.getRouterBaseURL}/show-gene-details/${gene}` };
        new_row['marked'] = false;
        new_row_data.push(new_row);
    }
    store.dispatch('setAGGRowdata', { data: new_row_data, id: props?.table_id });
    store.dispatch('setAGGRowkeys', { keys: Object.keys(list_to_use), id: props?.table_id });
}

/**
 * Renders the position details of a specific gene (chrN:S-E)
 * @param {Object} pos_obj - Object containing start/end positions and chromosome
 * @param {String} size - Either "full" (printed on one line) or "small" (printed with linebreak)
 */
const renderPosition = (pos_obj, size = 'full') => {
    try {
        if (size == "full")
            return (!pos_obj.start || !pos_obj.end || !pos_obj.chromosome) ? 'Not provided' :
                pos_obj.chromosome + ':' + pos_obj.start + '-' + pos_obj.end;
        else {
            return (!pos_obj.start || !pos_obj.end || !pos_obj.chromosome) ? 'Not provided' :
                pos_obj.chromosome + '<br>' + pos_obj.start + '-' + pos_obj.end;
        }
    } catch(error) {
        console.log("Error in gene table: "+error)
    }
}

/**
 * Updates current gene data on results update.
 * To check if the results changed, the result object keys are compared
 */
watch(() => current_genes.value, () => {
    if (Object.keys(current_genes.value).length > 0 && (row_keys.value != Object.keys(current_genes.value) || !row_keys.value)) {
        genesToRowData();
        gridOptions.api?.refreshCells();
        gridOptions.api?.sizeColumnsToFit();
        gridOptions.api?.paginationGoToPage(0);
    }
});

/**
 * Redraws the grid once the loading process stops.
 * Somehow refreshCells() is not enough here, so all rows have to be redrawn.
 */
watch(() => store.getters.getProcessState('load-results'), () => {
    gridOptions.api?.redrawRows();
    gridOptions.api?.sizeColumnsToFit();
});

const shortened_headers = {
    'gene': 'G.',
    'select': 'S.',
    'details': 'Dt.',
    'civic_url': 'Cv.',
    'position': 'Pos.',
    'treatments': 'Tr.',
    'entrez_id': 'Ent.',
}
const long_headers = {
    'gene': 'Gene',
    'select': 'Select',
    'details': 'Details',
    'civic_url': 'CIViC Gene ID',
    'position': 'Position',
    'treatments': 'Treatments',
    'entrez_id': 'Entrez ID',
}
</script>

<style>
.ag-theme-alpine {
    --ag-border-color: var(--dark-grey);
    overflow: hidden;
    --details-min-width: 150;
}

.ag-row .ag-cell {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
}

::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
}

.details-button {
    display: flex;
    background-color: #fff;
    color: #000;
    border-radius: 5px;
    padding-left: 16px;
    padding-right: 16px;
    width: fit-content;
    height: 32px;
    justify-items: center;
    align-items: center;
    margin: auto;
    position: absolute;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
    box-shadow: rgba(14, 30, 37, 0.12) 0px 2px 4px 0px, rgba(14, 30, 37, 0.32) 0px 2px 2px 0px;
    font-family: var(--pure-material-font, "Roboto", "Segoe UI", BlinkMacSystemFont, system-ui, -apple-system);
}

.details-button:hover {
    background-color: #f3f3f3;
    cursor: pointer;
}

.details-button-disabled {
    display: flex;
    background-color: #f0f0f0;
    color: #d5d5d5;
    border-radius: 5px;
    padding-left: 16px;
    padding-right: 16px;
    width: fit-content;
    height: 32px;
    justify-items: center;
    align-items: center;
    margin: auto;
    position: absolute;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
    font-family: var(--pure-material-font, "Roboto", "Segoe UI", BlinkMacSystemFont, system-ui, -apple-system);
}

.presentation-header * {
    text-align: center;
    justify-content: center;
}

.presentation-header-left * {
    text-align: left;
    justify-content: left;
}

.load-icon {
    margin-left: 24px;
}

.details-button-small {
    width: 40px !important;
    padding: 0 !important;
    justify-content: center !important;
}

@media (max-width: 1300px) {

    div.ag-theme-alpine div.ag-row {
        font-size: 0.7rem !important;
    }

}

@media (max-width: 1000px) {

    div.ag-theme-alpine div.ag-row {
        font-size: 0.6rem !important;
    }

}
</style>

